/* eslint-disable react-refresh/only-export-components -- constants only; .tsx for the icons */
import { Box, type AutocompleteRenderGroupParams } from '@mui/material';
import {
  AltRoute, Hub, Language, LocalOffer, Sensors, WebAsset,
} from '@mui/icons-material';

/**
 * Shared by the report and the per-user config, so a saved default can only ever
 * name a dimension the report can actually render. `short` keeps all six shortcut
 * buttons on one line of a phone.
 */
export const MAIN_GROUPINGS = [
  { value: 'traffic_source_id', label: 'Traffic Sources', short: 'TS', icon: <Sensors sx={{ fontSize: 16 }} /> },
  { value: 'flow_id', label: 'Flows', short: 'Flows', icon: <AltRoute sx={{ fontSize: 16 }} /> },
  { value: 'network_id', label: 'Networks', short: 'Net', icon: <Hub sx={{ fontSize: 16 }} /> },
  { value: 'landing_page_id', label: 'Landing Pages', short: 'LPs', icon: <WebAsset sx={{ fontSize: 16 }} /> },
  { value: 'offer_id', label: 'Offers', short: 'Offers', icon: <LocalOffer sx={{ fontSize: 16 }} /> },
  { value: 'region', label: 'State / Region', short: 'Region', icon: <Language sx={{ fontSize: 16 }} /> },
] as const;

export const V_GROUPINGS = [
  { value: 'v1', label: 'V1' },
  { value: 'v2', label: 'V2' },
  { value: 'v3', label: 'V3' },
  { value: 'v4', label: 'V4' },
  { value: 'v5', label: 'V5' },
  { value: 'v6', label: 'V6' },
  { value: 'v7', label: 'V7' },
  { value: 'v8', label: 'V8' },
  { value: 'v9', label: 'V9' },
  { value: 'v10', label: 'V10' },
];

export const TIME_GROUPINGS = [
  { value: 'date', label: 'Date' },
  { value: 'hour', label: 'Hour of Day' },
];

export const DIMENSION_GROUPINGS = [
  { value: 'device', label: 'Device Type' },
  { value: 'os', label: 'OS' },
  { value: 'os_version', label: 'OS Version' },
  { value: 'language', label: 'Language' },
  { value: 'country', label: 'Countries' },
  { value: 'referrer_domain', label: 'Referrer Domain' },
  { value: 'user_agent', label: 'User Agent' },
  { value: 'external_id_type', label: 'Source' },
];

/**
 * The dropdown order. Sections must stay contiguous: MUI's Autocomplete groups by
 * adjacency, so a stray option opens a second heading with the same name.
 */
export const OPTION_SECTIONS = [
  { section: 'Time', options: TIME_GROUPINGS },
  { section: 'Entities', options: [
    ...MAIN_GROUPINGS.filter(g => g.value !== 'region').map(g => ({ value: g.value as string, label: g.label as string })),
    { value: 'traffic_source_kind', label: 'Traffic Source Kind' },
    { value: 'network_kind', label: 'Network Kind' },
    { value: 'workspace_id', label: 'Workspace' },
  ] },
  { section: 'Variables', options: V_GROUPINGS },
  { section: 'Other', options: [
    { value: 'region', label: 'State / Region' },
    ...DIMENSION_GROUPINGS,
  ] },
];

export const ALL_GROUP_OPTIONS = OPTION_SECTIONS.flatMap(s => s.options);

/** Mirror of the server's COST_ACCURATE_GROUP_BYS (tracking-query.ts) — keep in sync. The report
 *  response's `cost_basis` is the authority; this only drives the group-by picker's pre-request hint. */
export const COST_ACCURATE_GROUP_BYS = new Set(['date', 'hour', 'date_hour', 'flow_id', 'traffic_source_id', 'workspace_id', 'traffic_source_kind']);
export const isCostEstimated = (value: string) => !COST_ACCURATE_GROUP_BYS.has(value);
export const EST_COST_NOTE = 'Estimated: the platform reports spend above this grouping, so cost is apportioned '
  + 'across its rows. Treat profit/ROI as indicative — group by workspace, traffic source, flow or date for actual cost.';

const SECTION_OF: Record<string, string> = Object.fromEntries(
  OPTION_SECTIONS.flatMap(s => s.options.map(o => [o.value, s.section])),
);

/** '' for anything unlisted — the sentinel "None"/"Default" options included. */
export const sectionOf = (value: string) => SECTION_OF[value] ?? '';

/** An empty group name skips the heading, leaving the leading "None"/"Default" unheaded. */
export const renderOptionGroup = (params: AutocompleteRenderGroupParams) => (params.group
  ? [
    <Box component="li" key={`${params.key}-h`}
      sx={{ px: 1.25, pt: 0.75, pb: 0.25, fontSize: '0.6875rem', fontWeight: 700, lineHeight: 1.2,
        color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5,
        position: 'sticky', top: -8, bgcolor: 'background.paper', zIndex: 1 }}>
      {params.group}
    </Box>,
    params.children,
  ]
  : params.children);

export const DEFAULT_GROUP_BY = 'date';
export const DEFAULT_DRILLDOWN_GROUP_BY = 'traffic_source_id';
export const DEFAULT_DRILLDOWN_GROUP_BY_2 = '';
export const DEFAULT_DRILLDOWN_GROUP_BY_3 = '';

export function isGroupingValue(v: unknown): v is string {
  return typeof v === 'string' && ALL_GROUP_OPTIONS.some(g => g.value === v);
}

// Mirror of mx-validation-library's MAX_DIMS_PER_LEVEL / isLevelOf; the client doesn't depend on it.
export const MAX_DIMS_PER_LEVEL = 3;

export function isLevelValue(v: unknown): v is string {
  if (typeof v !== 'string') return false;
  const dims = v.split('+');
  return dims.length <= MAX_DIMS_PER_LEVEL && new Set(dims).size === dims.length && dims.every(isGroupingValue);
}
